import { Button, Modal, Radio } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgKebab } from '@common/icon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { AuthMutation } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { SELECTOR } from '@/constants';

import { useDeleteCommentMutation } from './_hooks/useDeleteCommentMutation';

export interface CommentMenuProps {
  isMyComment: boolean;
  commentId: number;
  onClickEditButton?: (commentId: number) => void;
}

enum MODAL_TYPE {
  DELETE = 'DELETE',
}

enum DRAWER_VIEW {
  MENU = 'MENU',
  REPORT = 'REPORT',
}

const REPORT_REASONS = [
  '홍보성 광고글',
  '음란성, 선정성 글',
  '욕설',
  '개인정보 노출',
  '반복 게시글',
  '기타',
];

export const CommentMenu = ({
  isMyComment,
  commentId,
  onClickEditButton,
}: CommentMenuProps) => {
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const [drawerView, setDrawerView] = useState<DRAWER_VIEW>(DRAWER_VIEW.MENU);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>();

  const closeAll = () => {
    setIsDrawerOpened(false);
    setIsModalOpened(false);
    setDrawerView(DRAWER_VIEW.MENU);
    setSelectedReason(undefined);
  };

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [modalType, setModalType] = useState(MODAL_TYPE.DELETE);

  const postCommentReportMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/wello-comment-report'](),
    onSuccess: async (_, { body }) => {
      const commentId = SELECTOR.COMMENT_PREFIX + body?.welloCommentId;

      await queryClient.invalidateQueries();

      scroller.scrollTo(String(commentId), {
        offset: -100,
      });

      closeAll();

      toast({
        message: '댓글이 신고되었습니다.',
      });
    },
  });

  const deleteCommentMutation = useDeleteCommentMutation();

  return (
    <>
      <Button
        className="flex size-26 items-center justify-center p-0"
        dimensions="H26"
        theme="sub-text"
        onClick={() => {
          setDrawerView(DRAWER_VIEW.MENU);
          setIsDrawerOpened(true);
        }}
      >
        <SvgKebab className="size-14 text-Gray-700" />
      </Button>
      <ResponsiveDialog
        opened={isDrawerOpened}
        onClose={closeAll}
        drawerType="bottom"
        className={{
          backdrop: 'z-30',
          drawer: 'z-40',
        }}
      >
          {drawerView === DRAWER_VIEW.MENU ? (
            <ul className="my-16 flex flex-col gap-16">
              {isMyComment ? (
                <>
                  {onClickEditButton ? (
                    <li className="w-full">
                      <Button
                        className="w-full"
                        onClick={() => onClickEditButton(commentId)}
                      >
                        수정하기
                      </Button>
                    </li>
                  ) : null}
                  <li className="w-full">
                    <Button
                      className="w-full text-R-500"
                      theme="primary-line"
                      onClick={() => {
                        setModalType(MODAL_TYPE.DELETE);
                        setIsDrawerOpened(false);
                        setIsModalOpened(true);
                      }}
                    >
                      삭제하기
                    </Button>
                  </li>
                </>
              ) : (
                <li className="flex w-full flex-col gap-8">
                  <Button
                    className="w-full text-Gray-900"
                    theme="primary-line"
                    onClick={() => {
                      // 신고는 기존 메뉴 drawer를 재사용해 오버레이가 중첩되지 않게 한다.
                      setSelectedReason(undefined);
                      setDrawerView(DRAWER_VIEW.REPORT);
                    }}
                  >
                    댓글 신고하기
                  </Button>
                </li>
              )}
            </ul>
          ) : (
            <form
              className="flex flex-col gap-30"
              onSubmit={(e) => {
                e.preventDefault();

                if (selectedReason) {
                  postCommentReportMutation.mutate({
                    body: {
                      welloCommentId: commentId,
                      contents: selectedReason,
                    },
                  });
                }
              }}
            >
              <h2 className="font-700 text-Gray-900 typo-body1">
                신고하는 이유를 선택해주세요.
              </h2>
              <Radio.Group
                className="flex-col gap-20"
                value={selectedReason}
                onChange={(value) => setSelectedReason(value)}
              >
                {REPORT_REASONS.map((reason) => (
                  <Radio.Item
                    key={reason}
                    className="justify-start typo-body3"
                    value={reason}
                  >
                    {reason}
                  </Radio.Item>
                ))}
              </Radio.Group>
              <Button
                className="w-full"
                disabled={!selectedReason}
                loading={postCommentReportMutation.isPending}
                type="submit"
              >
                선택 완료
              </Button>
            </form>
          )}
      </ResponsiveDialog>
      <Modal
        className="gap-16"
        opened={isModalOpened}
        onClose={() => setIsModalOpened(false)}
      >
        <h2 className="text-Gray-900 typo-body1">
          {
            {
              [MODAL_TYPE.DELETE]: '댓글을 삭제하시겠습니까?',
            }[modalType]
          }
        </h2>
        <footer className="mt-24 flex gap-8">
          <Button
            className="flex-1"
            theme="primary-line"
            onClick={() => {
              setIsModalOpened(false);
            }}
          >
            취소
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              switch (modalType) {
                case MODAL_TYPE.DELETE:
                  deleteCommentMutation.mutate({
                    path: {
                      welloCommentId: commentId,
                    },
                  });
                  break;
              }
            }}
          >
            {
              {
                [MODAL_TYPE.DELETE]: '삭제',
              }[modalType]
            }
          </Button>
        </footer>
      </Modal>
    </>
  );
};
